// src/components/layout/BottomNav.js
import React, { useState, useEffect, useRef } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { theme } from '../../lib/theme'

export default function BottomNav({ onAddTransaction }) {
  const navigate = useNavigate()
  const [showMore, setShowMore] = useState(false)
  const sheetRef = useRef(null)

  // Close sheet on escape
  useEffect(() => {
    if (!showMore) return
    const handler = (e) => { if (e.key === 'Escape') setShowMore(false) }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [showMore])

  const handleSignOut = async () => {
    setShowMore(false)
    await supabase.auth.signOut()
    navigate('/login')
  }

  const goTo = (path) => {
    setShowMore(false)
    navigate(path)
  }

  const moreItems = [
    { label: 'Net Worth',      path: '/networth' },
    { label: 'Money Movement', path: '/money-movement' },
    { label: 'Debts',          path: '/debts' },
    { label: 'Check-ins', path: '/checkins' },
    { label: 'Reports',   path: '/report' },
    { label: 'Settings',  path: '/settings' },
    { label: 'Help',      path: '/help' },
  ]

  return (
    <>
      <nav style={styles.nav} aria-label="Primary">
        <NavTab to="/" end label="Home" />
        <NavTab to="/plan" label="Plan" />

        <button
          type="button"
          className="btn-compact"
          style={styles.fabWrapper}
          onClick={onAddTransaction}
          aria-label="Add transaction"
        >
          <div style={styles.fab}>
            <ScanIcon size={24} />
          </div>
          <span style={styles.fabLabel}>Scan</span>
        </button>

        <NavTab to="/log" label="Log" />

        <button
          type="button"
          className="btn-compact"
          style={{ ...styles.tab, color: showMore ? theme.colors.plum : theme.colors.inkMuted }}
          onClick={() => setShowMore(true)}
          aria-haspopup="dialog"
          aria-expanded={showMore}
        >
          <span style={styles.tabLabel}>More</span>
        </button>
      </nav>

      {/* Overflow sheet */}
      {showMore && (
        <div
          style={styles.sheetBackdrop}
          onClick={(e) => { if (e.target === e.currentTarget) setShowMore(false) }}
          role="dialog"
          aria-modal="true"
          aria-label="More options"
        >
          <div style={styles.sheet} ref={sheetRef}>
            <div style={styles.sheetHandle} aria-hidden="true" />
            <div style={styles.sheetHeader}>
              <span style={styles.sheetTitle}>More</span>
              <button
                type="button"
                className="btn-compact"
                style={styles.sheetClose}
                onClick={() => setShowMore(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div style={styles.sheetList}>
              {moreItems.map(item => (
                <button
                  key={item.path}
                  type="button"
                  className="btn-compact"
                  style={styles.sheetItem}
                  onClick={() => goTo(item.path)}
                >
                  <span style={styles.sheetItemLabel}>{item.label}</span>
                </button>
              ))}
              <div style={styles.sheetDivider} />
              <button
                type="button"
                className="btn-compact"
                style={{ ...styles.sheetItem, color: theme.colors.rose }}
                onClick={handleSignOut}
              >
                <span style={styles.sheetItemLabel}>Sign out</span>
              </button>
            </div>
            <div style={{ height: 'env(safe-area-inset-bottom, 8px)' }} />
          </div>
        </div>
      )}
    </>
  )
}

// ─────────────────────────────────────────────────────────────
// NavTab — small wrapper for the standard tabs (not the FAB)
// ─────────────────────────────────────────────────────────────

function NavTab({ to, end, label }) {
  return (
    <NavLink
      to={to}
      end={end}
      className="btn-compact"
      style={({ isActive }) => ({
        ...styles.tab,
        color: isActive ? theme.colors.plum : theme.colors.inkMuted,
        fontWeight: isActive ? 700 : 600,
      })}
      aria-label={label}
    >
      <span style={styles.tabLabel}>{label}</span>
    </NavLink>
  )
}

// ─────────────────────────────────────────────────────────────
// Scan icon — hand-written inline SVG (scan-frame outline).
// The one retained icon in the app (No-Emoji-No-Lucide Rule).
// Inherits color from the FAB via currentColor.
// ─────────────────────────────────────────────────────────────

function ScanIcon({ size = 24 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 7V5a2 2 0 0 1 2-2h2" />
      <path d="M17 3h2a2 2 0 0 1 2 2v2" />
      <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
      <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
      <line x1="4" y1="12" x2="20" y2="12" />
    </svg>
  )
}

// ─────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────

const c = theme.colors
const f = theme.fonts

const styles = {
  nav: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    background: 'rgba(250, 247, 242, 0.92)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    borderTop: `1px solid ${c.line}`,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr',
    alignItems: 'center',
    padding: '10px 8px calc(env(safe-area-inset-bottom, 8px) + 8px)',
    zIndex: 100,
    fontFamily: f.body,
  },
  tab: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    minHeight: '44px',
    color: c.inkMuted,
    textDecoration: 'none',
    background: 'transparent',
    border: 'none',
    padding: '6px 4px',
    cursor: 'pointer',
    fontFamily: f.body,
    transition: 'color 120ms ease',
  },
  tabLabel: {
    fontSize: '12.5px',
    fontWeight: 600,
    letterSpacing: '0.01em',
  },

  // Center scan FAB
  fabWrapper: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
    fontFamily: f.body,
  },
  fab: {
    width: '52px',
    height: '52px',
    borderRadius: '50%',
    background: c.plum,
    color: c.cream,
    display: 'grid',
    placeItems: 'center',
    marginTop: '-18px',
    boxShadow: '0 6px 18px -4px rgba(30, 16, 64, 0.4), 0 2px 4px rgba(30, 16, 64, 0.15)',
    border: `3px solid ${c.cream}`,
    transition: 'transform 100ms ease',
  },
  fabLabel: {
    fontSize: '10.5px',
    fontWeight: 600,
    color: c.plum,
  },

  // Sheet
  sheetBackdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(30, 16, 64, 0.4)',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    animation: 'none',
  },
  sheet: {
    background: c.white,
    borderRadius: '20px 20px 0 0',
    width: '100%',
    maxWidth: '500px',
    boxShadow: '0 -8px 40px rgba(30, 16, 64, 0.2)',
    paddingBottom: '8px',
  },
  sheetHandle: {
    width: '40px',
    height: '4px',
    borderRadius: '2px',
    background: c.cream3,
    margin: '10px auto 0',
  },
  sheetHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 20px 6px',
  },
  sheetTitle: {
    fontFamily: f.display,
    fontSize: '20px',
    fontWeight: 600,
    color: c.plum,
    letterSpacing: '-0.02em',
  },
  sheetClose: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    background: c.cream2,
    border: 'none',
    color: c.plum,
    cursor: 'pointer',
    display: 'grid',
    placeItems: 'center',
    fontSize: '20px',
    lineHeight: 1,
  },
  sheetList: {
    display: 'flex',
    flexDirection: 'column',
    padding: '8px 12px 4px',
  },
  sheetItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    padding: '14px',
    background: 'transparent',
    border: 'none',
    color: c.plum,
    fontFamily: f.body,
    fontSize: '15px',
    fontWeight: 500,
    cursor: 'pointer',
    width: '100%',
    textAlign: 'left',
    borderRadius: '10px',
    transition: 'background 120ms ease',
  },
  sheetItemIcon: {
    color: c.plumSoft,
    flexShrink: 0,
  },
  sheetItemLabel: {
    flex: 1,
  },
  sheetDivider: {
    height: '1px',
    background: c.line,
    margin: '6px 14px',
  },
}
